import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import ModalDialog from './ModalDialog';
import Login from './Login';
import { 
  Container, 
  Button, 
  Grid2, 
  CircularProgress, 
  Typography, 
  Switch, 
  Card, 
  CardContent, 
  Box, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();

  // Function to load token from localStorage
  const loadToken = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const storedToken = userData?.token || '';
    setToken(storedToken);
    if (!storedToken) {
      setOpenModal(true);
    } else {
      setOpenModal(false);
    }
  };

  // Load token on mount and listen for login events
  useEffect(() => {
    loadToken();

    const handleUserLoggedIn = () => {
      loadToken();
    };

    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
    };
  }, []);

  useEffect(() => {
    if (token) {
      const currentDate = new Date();
      axios.get(`${process.env.REACT_APP_API_URL}/api/apartments/`, {
        headers: { 'Authorization': `Token ${token}` }
      })
        .then(response => {
          const sortedApartments = response.data.sort((a, b) => a.id - b.id);
          Promise.all(
            sortedApartments.map(apartment =>
              axios.get(`${process.env.REACT_APP_API_URL}/api/renthistories-apartment/${apartment.id}/`, {
                headers: { 'Authorization': `Token ${token}` }
              })
                .then(rentResponse => {
                  const activeRent = rentResponse.data.find(rent => {
                    const startDate = new Date(rent.rent_start);
                    const endDate = new Date(rent.rent_end);
                    return startDate <= currentDate && currentDate <= endDate;
                  });
                  return { ...apartment, activeRent };
                })
                .catch(error => {
                  console.error(`Error fetching rent histories for apartment ${apartment.id}:`, error);
                  return { ...apartment, activeRent: null };
                })
            )
          )
            .then(updatedApartments => {
              setApartments(updatedApartments);
              setLoading(false);
            })
            .catch(error => {
              console.error('Error processing rent histories:', error);
              setApartments(sortedApartments);
              setLoading(false);
            });
        })
        .catch(error => {
          console.error('There was an error fetching the apartments!', error);
          setLoading(false);
        });
    }
  }, [token]);

  const handlePublishToggle = async (apartmentId, isCurrentlyPublished) => {
    if (!isCurrentlyPublished) {
      setSelectedApartmentId(apartmentId);
      setIsDialogOpen(true);
    } else {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/apartments/${apartmentId}/update_is_published/`,
          { is_published: !isCurrentlyPublished },
          { headers: { 'Authorization': `Token ${token}` } }
        );
        setApartments(apartments.map(apartment =>
          apartment.id === apartmentId ? { ...apartment, is_published: response.data.is_published } : apartment
        ));
      } catch (error) {
        console.error('Error publishing/unpublishing the apartment:', error);
      }
    }
  };

  const handleRemoveApartment = async (apartmentId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/apartments/${apartmentId}/`, {
        headers: { 'Authorization': `Token ${token}` }
      });
      setApartments(apartments.filter(apartment => apartment.id !== apartmentId));
      alert(t('dashboard.assetRemovedSuccess'));
    } catch (error) {
      console.error('Error removing apartment:', error);
    }
  };

  const handleCardClick = (id) => {
    navigate(`/rent-history/${id}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container sx={{ flexGrow: 1, py: 2, flex: '1 0 auto' }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" component="h6">{t('dashboard.yourRentals')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('dashboard.feedbackRequest')}{' '}
            <a href="mailto:contact@rentlord.online" style={{ color: 'blue', textDecoration: 'underline' }}>
              contact@rentlord.online
            </a>.
          </Typography>
        </Box>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
        </Box>
        {loading ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid2 container spacing={3}>
            {apartments.map((apartment, index) => (
              <Grid2 item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: { xs: '100%', sm: 300, md: 300 }, // Fixed width for consistency
                  minWidth: { xs: '100%', sm: 300, md: 330 }, // Safety net
                  maxWidth: { xs: '100%', sm: 300, md: 330 }, // Upper limit
                  objectPosition: 'center',
                  height: 420,
                  boxSizing: 'border-box', // Include padding/border in width
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'scale(1.05)' },
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => handleCardClick(apartment.id)}
              >
                <Box
                  sx={{
                    height: 200,
                    width: '100%', // Match Card width
                    backgroundColor: 'grey.300',
                    overflow: 'hidden',
                    flexShrink: 0, // Prevent shrinking
                    boxSizing: 'border-box',
                    objectPosition: 'center'
                  }}
                >
                  <img
                    src={
                      apartment.photoURL && apartment.photoURL.length > 0
                        ? apartment.photoURL[0].photoURL
                        : '/placeholder.png'
                    }
                    alt={apartment.place_description || 'Apartment'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                    onError={(e) => (e.target.src = '/placeholder.png')}
                  />
                </Box>
                <CardContent
                  sx={{
                    flexGrow: 1, // Take remaining space
                    width: { xs: '100%', sm: 325, md: 325 }, // Match Card width exactly
                    boxSizing: 'border-box', // Include padding in width
                    padding: '16px',
                    '&:last-child': { paddingBottom: '16px' }, // Override MUI default
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    flexShrink: 0, // Prevent content from shrinking the card
                  }}
                >
                  <Box
                    sx={{
                      height: 28,
                      mb: 1,
                      width: '100%', // Ensure Box takes full width
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography
                      variant="h7"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%', // Force full width of parent Box
                        lineHeight: 1.9,
                      }}
                    >
                      {apartment.place_description || t('dashboard.na')}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 40, mb: 1 }}>
                    {apartment.activeRent ? (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ textAlign: 'left', color: 'secondary.main', mb: 1 }}
                        >
                          Tenant: {apartment.activeRent.tenant_name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textAlign: 'left', color: 'secondary.main' }}
                        >
                          Contract End: {new Date(apartment.activeRent.rent_end).toLocaleDateString()}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ textAlign: 'left' }}
                      >
                        {t('dashboard.availableStatus')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mt: 2 }}>
                    <Switch
                      checked={apartment.is_published}
                      onChange={(e) => {
                        e.stopPropagation();
                        handlePublishToggle(apartment.id, apartment.is_published);
                      }}
                      color={apartment.is_published ? 'success' : 'default'}
                    />
                    <Typography variant="body2" color={apartment.is_published ? 'green' : 'grey'}>
                      {t('dashboard.publishLabel')}: {apartment.is_published ? t('dashboard.publishedStatus') : t('dashboard.notPublishedStatus')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={Link}
                      to={`/edit-apartment/${apartment.id}`}
                      startIcon={<EditIcon />}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ flex: 1 }}
                    >
                      {t('dashboard.editButton')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveApartment(apartment.id);
                      }}
                      startIcon={<DeleteIcon />}
                      size="small"
                      sx={{ flex: 1 }}
                    >
                      {t('dashboard.removeButton')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
            ))}
            <Grid2
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <Card
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: { xs: '100%', sm: 300, md: 300 },
                  height: 420,
                  cursor: 'pointer',
                  border: '2px dashed grey',
                  '&:hover': {
                    backgroundColor: 'grey.200',
                  },
                }}
                onClick={() => navigate('/add-apartment')}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <AddIcon sx={{ fontSize: 60, color: 'grey.500' }} />
                  <Typography variant="h6" sx={{ color: 'grey.700' }}>
                    {t('dashboard.addApartmentButton')}
                  </Typography>
                </Box>
              </Card>
            </Grid2>
          </Grid2>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <ins
            className="adsbygoogle"
            style={{ display: 'block' }}
            data-ad-client="ca-pub-4558262338290493"
            data-ad-slot="9665979324"
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </Box>
      </Container>
      <Footer />
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('dashboard.confirmUnpublishTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('dashboard.confirmUnpublishMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} color="primary">
            {t('dashboard.cancelButton')}
          </Button>
          <Button
            onClick={() => {
              // The logic to unpublish is handled here now.
              if (selectedApartmentId) {
                // Find the apartment to get its current `is_published` state
                const apartment = apartments.find(ap => ap.id === selectedApartmentId);
                if (apartment) {
                  // Call the toggle function, now it will proceed to unpublish
                  handlePublishToggle(selectedApartmentId, true); // Passing true to simulate it's currently published
                }
              }
              setIsDialogOpen(false);
            }}
            color="primary"
            autoFocus
          >
            {t('dashboard.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    {/* Login Modal */}
     <ModalDialog open={openModal} onClose={() => setOpenModal(false)}>
        <Login onClose={() => setOpenModal(false)} />
      </ModalDialog>
    </div>
  );
};

export default Dashboard;